import React from 'react';
import { motion } from 'framer-motion';
import { FaBullseye, FaEye, FaStar, FaLightbulb, FaHandshake, FaHeart } from 'react-icons/fa';
import SEO from '../components/SEO';
import './About.css';

const About = () => {
  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '200+', label: 'Happy Clients' },
    { number: '50+', label: 'Team Members' },
    { number: '5+', label: 'Years Experience' }
  ];

  const values = [
    { icon: <FaStar />, title: 'Excellence', desc: 'We strive for excellence in every project' },
    { icon: <FaLightbulb />, title: 'Innovation', desc: 'Constantly pushing boundaries with creative solutions' },
    { icon: <FaHandshake />, title: 'Integrity', desc: 'Building trust through transparency and honesty' },
    { icon: <FaHeart />, title: 'Customer Focus', desc: 'Your success is our priority' }
  ];

  return (
    <div className="about-page">
      <SEO 
        title="About Us - HEGXIB Nigeria | Our Story & Mission"
        description="Learn about HEGXIB Nigeria's mission to provide innovative digital solutions. Discover our values, team, and commitment to excellence in technology services across Nigeria."
        keywords="about HEGXIB, company mission, technology team Nigeria, digital innovation, IT consulting team, our story"
        url="https://ng.hegxib.me/about"
      />
      {/* Page Header */}
      <motion.section 
        className="page-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            About HEGXIB Nigeria
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Driving Digital Innovation Across Nigeria
          </motion.p>
        </div>
      </motion.section>

      {/* About Content */}
      <section className="about-content">
        <div className="container">
          <motion.div 
            className="about-intro"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Who We Are</h2>
            <p>HEGXIB Nigeria is a leading technology and digital solutions provider committed to transforming the Nigerian business landscape. We specialize in delivering innovative, scalable, and efficient digital solutions that empower businesses to thrive in the modern digital economy.</p>
            <p>With a deep understanding of the Nigerian market and global technology trends, we bridge the gap between local businesses and world-class digital solutions.</p>
          </motion.div>

          <div className="mission-vision">
            <motion.div 
              className="mv-card"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -10 }}
            >
              <FaBullseye className="mv-icon" />
              <h3>Our Mission</h3>
              <p>To empower Nigerian businesses with cutting-edge digital solutions that drive growth, efficiency, and innovation in an increasingly connected world.</p>
            </motion.div>

            <motion.div 
              className="mv-card"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -10 }}
            >
              <FaEye className="mv-icon" />
              <h3>Our Vision</h3>
              <p>To be Nigeria's most trusted technology partner, recognized for excellence, innovation, and transformative impact on businesses across all sectors.</p>
            </motion.div>
          </div>

          <motion.div 
            className="values"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Core Values</h2>
            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-item"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h4>{value.title}</h4>
                  <p>{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="container">
          <motion.div 
            className="stats-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item"
                initial={{ scale: 0.5, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.1 }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
